/**
 * Typed shapes for the API endpoints the UI consumes. These mirror the
 * Phase 3/4/6/7/8 worklog response contracts — kept in this file to avoid
 * scattering literal strings through components.
 */

export type ContractStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'signed'
  | 'declined'
  | 'void'
  | 'completed';

export const CONTRACT_STATUSES: ContractStatus[] = [
  'draft',
  'sent',
  'viewed',
  'signed',
  'declined',
  'void',
  'completed',
];

export interface VariableSchemaEntry {
  name: string;
  label?: string;
  required?: boolean;
  default?: string;
}

export interface TemplateSummary {
  id: string;
  name: string;
  description: string | null;
  variable_schema: VariableSchemaEntry[] | null;
  created_at: string;
  updated_at: string;
}

export interface TemplateDetail extends TemplateSummary {
  body: string;
}

export interface ContractSummary {
  id: string;
  template_id: string | null;
  status: ContractStatus;
  created_at: string;
  sent_at: string | null;
  metadata: Record<string, string> | null;
}

export interface ContractDetail {
  id: string;
  account_id: string;
  template_id: string | null;
  template_updated_at: string | null;
  template_snapshot: string | null;
  merge_data: Record<string, unknown> | null;
  metadata: Record<string, string> | null;
  status: ContractStatus;
  completion_url: string | null;
  created_at: string;
  created_by: string | null;
  sent_at: string | null;
  signed_at: string | null;
  voided_at: string | null;
  declined_at: string | null;
  completed_at: string | null;
}

export interface AuditEvent {
  id: string;
  seq: number;
  event_type: string;
  actor_type: string | null;
  actor_id: string | null;
  occurred_at: string;
  payload: unknown;
  prev_hash: string;
  event_hash: string;
}

export interface AuditResponse {
  events: AuditEvent[];
  chain_valid: boolean;
  first_invalid_seq: number | null;
}

export interface AccountInfo {
  id: string;
  name: string | null;
  slug: string;
  custom_domain: string | null;
  custom_domain_status: 'none' | 'pending' | 'active' | 'failed';
  custom_domain_checked_at: string | null;
  webhook_url: string | null;
  branding: Record<string, unknown> | null;
  status: 'active' | 'suspended';
  is_sandbox: boolean;
  quota_contracts_per_day: number;
}

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  last4: string;
  created_at: string;
  revoked_at: string | null;
  last_used_at: string | null;
}

export interface SandboxCreatedResponse {
  sandbox_account: {
    id: string;
    slug: string;
    name: string;
    is_sandbox: boolean;
    parent_account_id: string;
  };
  api_key: {
    id: string;
    name: string;
    created_at: string;
    key: string;
    prefix: string;
  };
}

export interface SendResponse {
  contract_id: string;
  signer_id: string;
  signing_url: string;
  token_expires_at: string;
}
